import { movies } from "@/mocks/movies";
import type { Movie } from "@/mocks/movies";

export type SortKey = "updated" | "hot" | "rating" | "relevance";

export type FilterState = {
  type?: string;
  region?: string;
  year?: string;
  sort?: SortKey;
};

export const PAGE_SIZE = 8;

export function getAllMovies(): Movie[] {
  return movies;
}

export function getMovieById(id?: string): Movie | undefined {
  return movies.find((movie) => movie.id === id);
}

export function sortMovies(list: Movie[], sort: SortKey = "updated"): Movie[] {
  const copy = [...list];
  if (sort === "hot") {
    return copy.sort((a, b) => b.views - a.views);
  }
  if (sort === "rating") {
    return copy.sort((a, b) => b.rating - a.rating);
  }
  return copy.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export function matchYear(movieYear: number, year?: string): boolean {
  if (!year || year === "全部") return true;
  if (year === "更早") return movieYear < 2020;
  return movieYear === Number(year);
}

export function filterMovies({ type, region, year, sort }: FilterState): Movie[] {
  const filtered = movies.filter((movie) => {
    const typeOk = !type || type === "全部" || movie.genres.includes(type);
    const regionOk = !region || region === "全部" || movie.region === region;
    return typeOk && regionOk && matchYear(movie.year, year);
  });
  return sortMovies(filtered, sort ?? "updated");
}

export function searchMovies(keyword: string, sort: SortKey = "relevance"): Movie[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  const matched = movies.filter((movie) => {
    const haystack = [
      movie.title,
      movie.enTitle,
      movie.director,
      movie.region,
      ...movie.genres,
      ...movie.cast,
      ...movie.tags,
      movie.synopsis,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
  if (sort === "updated") return sortMovies(matched, "updated");
  if (sort === "hot") return sortMovies(matched, "hot");
  if (sort === "rating") return sortMovies(matched, "rating");
  return matched.sort((a, b) => {
    const aTitle = a.title.includes(keyword) ? 1 : 0;
    const bTitle = b.title.includes(keyword) ? 1 : 0;
    if (aTitle !== bTitle) return bTitle - aTitle;
    return b.rating - a.rating;
  });
}

export function getRelatedMovies(movie: Movie, limit = 4): Movie[] {
  const scored = movies
    .filter((item) => item.id !== movie.id)
    .map((item) => {
      const genreScore = item.genres.filter((g) => movie.genres.includes(g)).length * 3;
      const directorScore = item.director === movie.director ? 5 : 0;
      const regionScore = item.region === movie.region ? 1 : 0;
      return { item, score: genreScore + directorScore + regionScore };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function getLatestMovies(limit = 8): Movie[] {
  return sortMovies(movies, "updated").slice(0, limit);
}

export function getHotMovies(limit = 6): Movie[] {
  return sortMovies(movies, "hot").slice(0, limit);
}

export function paginate<T>(list: T[], page: number, size = PAGE_SIZE) {
  const totalPages = Math.max(1, Math.ceil(list.length / size));
  const current = Math.min(Math.max(1, page), totalPages);
  const start = (current - 1) * size;
  return {
    items: list.slice(start, start + size),
    totalPages,
    currentPage: current,
    totalItems: list.length,
  };
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours > 0 ? `${hours} 小时 ${rest} 分` : `${rest} 分钟`;
}

export function formatUpdated(date: string): string {
  const [year, month, day] = date.split("-");
  return `${year} 年 ${Number(month)} 月 ${Number(day)} 日`;
}